import { BrowserModule, Title } from "@angular/platform-browser";
import { NgModule } from "@angular/core";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HomeComponent } from "./home/home.component";
import { SharedModule } from "./shared/shared.module";
import { AuthModule } from "./auth/auth.module";
import { provideHttpClient, withInterceptors, withXhr } from "@angular/common/http";
import {
  appAuthInterceptor,
  appCheckInterceptor,
  appLoaderInterceptor,
  appLoggingInterceptor,
} from "./shared/http.interceptors";
import { LoaderComponent } from "./loaderSpinner/loader.component";
import { TypesComponent } from "./types/types.component";
import { ItemEditComponent } from "./item-edit/item-edit.component";
import { StatusesComponent } from "./statuses/statuses/statuses.component";
import { AllowedStatusesEditComponent } from "./statuses/allowed-statuses-edit/allowed-statuses-edit.component";
import { FormsComponent } from "./forms/forms.component";
import { ItemComponent } from "./forms/item/item.component";
import { FormEditComponent } from "./forms/form-edit/form-edit.component";
import { PrioritiesComponent } from "./priorities/priorities.component";
import { NgOptimizedImage, registerLocaleData } from "@angular/common";
import localePl from "@angular/common/locales/pl";
import { AreasComponent } from "./areas/areas.component";
import { ProductCategoriesComponent } from "./product-categories/product-categories.component";
import { TableFieldComponent } from "./forms/table-field/table-field.component";
import { ConfigurationComponent } from "./configuration/configuration.component";
import { BranchAdminsComponent } from "./branch-admins/branch-admins.component";
import { MatTableModule } from "@angular/material/table";
import { MatSortModule } from "@angular/material/sort";
import { BranchesComponent } from "./branches/branches.component";
import { BranchesAreasEditComponent } from "./branches/branches-areas-edit/branches-areas-edit.component";
import { FormsModule } from "@angular/forms";
import { DepartmentsComponent } from "./departments/departments.component";
import { FormTypesComponent } from "./form-types/form-types.component";
import { LibraryComponent } from "./library/library.component";
import { LibraryDocumentEditComponent } from "./library/library-document-edit/library-document-edit.component";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatTreeModule } from "@angular/material/tree";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { LibraryDirectoryEditComponent } from "./library/library-directory-edit/library-directory-edit.component";
import { ChainsComponent } from "./chains/chains.component";
import { ChainAdminsComponent } from "./chain-admins/chain-admins.component";
import { ChainsCustomersEditComponent } from "./chains/chains-customers-edit/chains-customers-edit.component";
import { AdminsListComponent } from "./users/admins-list/admins-list.component";
import { AdminEditComponent } from "./users/admin-edit/admin-edit.component";
import { UserAttributesComponent } from "./user-attributes/user-attributes.component";
import { InstallationAttributesComponent } from "./installation-attributes/installation-attributes.component";
import { StatusesOverviewComponent } from "./statuses/statuses-overview/statuses-overview.component";
import { StatusesSelectorComponent } from "./statuses/statuses-selector/statuses-selector.component";
import { StatusQuestionnaireEditComponent } from "./statuses/status-questionnaire-edit/status-questionnaire-edit.component";
import { CustomerSelectionComponent } from "./customer/customer-selection/customer-selection.component";
import { CustomerCardComponent } from "./customer/customer-card/customer-card.component";
import { MatPaginatorModule } from "@angular/material/paginator";
import { SequentialOrderActionsComponent } from "./sequential-order-actions/sequential-order-actions.component";
import { LibraryDocumentsTreeComponent } from "./library/library-documents-tree/library-documents-tree.component";
import { ProductCategoriesAttributeEditComponent } from "./product-categories/product-categories-attribute-edit/product-categories-attribute-edit.component";
import { TableFieldConditionDialogComponent } from "./forms/table-field/table-filed-condition-dialog/table-field-condition-dialog.component";
import { MatRadioModule } from "@angular/material/radio";
import { BranchEditDialogComponent } from "./branches/branch-edit-dialog/branch-edit-dialog.component";
import { FormsPermissionsComponent } from "./forms-permissions/forms-permissions.component";
import { FormAssignComponent } from "./forms-permissions/form-assign/form-assign.component";
import { FormDisplayComponent } from "./forms-permissions/form-display/form-display.component";
import { FormLoaderComponent } from "./forms-permissions/form-loader/form-loader.component";
import { FormPermissionsItemComponent } from "./forms-permissions/form-permissions-item/form-permissions-item.component";
import {
  MatStep,
  MatStepLabel,
  MatStepper,
  MatStepperNext,
  MatStepperPrevious,
} from "@angular/material/stepper";
import { CurrentAssignmentsComponent } from "./forms-permissions/current-assignments/current-assignments.component";
import { ColumnComponent } from "./forms-permissions/column/column.component";
import { ColorPickerComponent, ColorPickerDirective } from "ngx-color-picker";
import { MatIconModule } from "@angular/material/icon";
import { MatDividerModule } from "@angular/material/divider";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { DocumentViewerComponent } from "./shared/document-viewer/document-viewer.component";
import { FormAssignPermDialogComponent } from "./forms-permissions/form-assign-perm-dialog/form-assign-perm-dialog.component";
import { MatChipsModule } from "@angular/material/chips";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatAccordion, MatExpansionModule } from "@angular/material/expansion";

registerLocaleData(localePl);

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    LoaderComponent,
    TypesComponent,
    ItemEditComponent,
    StatusesComponent,
    AllowedStatusesEditComponent,
    FormsComponent,
    ItemComponent,
    FormEditComponent,
    PrioritiesComponent,
    AreasComponent,
    ProductCategoriesComponent,
    TableFieldComponent,
    ConfigurationComponent,
    BranchAdminsComponent,
    BranchesComponent,
    BranchesAreasEditComponent,
    DepartmentsComponent,
    FormTypesComponent,
    LibraryComponent,
    LibraryDocumentEditComponent,
    LibraryDirectoryEditComponent,
    ChainsComponent,
    ChainAdminsComponent,
    ChainsCustomersEditComponent,
    AdminsListComponent,
    AdminEditComponent,
    UserAttributesComponent,
    InstallationAttributesComponent,
    StatusesOverviewComponent,
    StatusesSelectorComponent,
    StatusQuestionnaireEditComponent,
    CustomerSelectionComponent,
    CustomerCardComponent,
    SequentialOrderActionsComponent,
    LibraryDocumentsTreeComponent,
    ProductCategoriesAttributeEditComponent,
    TableFieldConditionDialogComponent,
    BranchEditDialogComponent,
    FormsPermissionsComponent,
    FormAssignComponent,
    FormDisplayComponent,
    FormLoaderComponent,
    FormPermissionsItemComponent,
    CurrentAssignmentsComponent,
    ColumnComponent,
    DocumentViewerComponent,
    FormAssignPermDialogComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    AuthModule,
    SharedModule,
    MatTableModule,
    MatSortModule,
    FormsModule,
    MatProgressBarModule,
    MatTreeModule,
    DragDropModule,
    ColorPickerComponent,
    ColorPickerDirective,
    MatPaginatorModule,
    MatRadioModule,
    NgOptimizedImage,
    MatStepper,
    MatStep,
    MatStepperNext,
    MatStepLabel,
    MatStepperPrevious,
    MatIconModule,
    MatDividerModule,
    MatCardModule,
    MatFormFieldModule,
    MatChipsModule,
    MatTooltipModule,
    MatButtonToggleModule,
    MatAccordion,
    MatExpansionModule,
  ],
  providers: [
    Title,
    provideHttpClient(withXhr(), 
      withInterceptors([
        appLoggingInterceptor,
        appCheckInterceptor,
        appAuthInterceptor,
        appLoaderInterceptor,
      ]),
    ),
  ],
})
export class AppModule {}
